import React, { forwardRef, useImperativeHandle } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

export type IconMorphHandle = {
  trigger: () => void;
};

interface IconMorphProps {
  icons: React.ReactElement[];
  size?: number;
  strokeWidth?: number;
  stepDurationMs?: number; // interval between icon changes
  glitchDurationMs?: number; // how long each glitch runs
  transitionStyle?: 'glitch' | 'crossfade' | 'motion';
  className?: string;
  triggerMode?: 'self' | 'external';
}

const IconMorph = forwardRef<IconMorphHandle, IconMorphProps>(function IconMorph(
  {
    icons,
    size = 36,
    strokeWidth = 1.4,
    stepDurationMs = 260,
    glitchDurationMs = 600,
    transitionStyle = 'motion',
    className = '',
    triggerMode = 'self',
  },
  ref
) {
  const [activeIdx, setActiveIdx] = React.useState(0);
  const [prevIdx, setPrevIdx] = React.useState<number | null>(null);
  const [glitching, setGlitching] = React.useState(false);
  const runningRef = React.useRef(false);
  const timeoutsRef = React.useRef<number[]>([]);
  const currentIdxRef = React.useRef(0);

  React.useEffect(() => {
    currentIdxRef.current = activeIdx;
  }, [activeIdx]);

  const clearAll = () => {
    timeoutsRef.current.forEach((id) => window.clearTimeout(id));
    timeoutsRef.current = [];
  };

  React.useEffect(() => {
    return () => clearAll();
  }, []);

  const stepTo = (nextIdx: number) => {
    const prev = currentIdxRef.current;
    setPrevIdx(prev);
    setActiveIdx(nextIdx);
    if (transitionStyle === 'glitch') {
      setGlitching(true);
      const id = window.setTimeout(() => {
        setGlitching(false);
        setPrevIdx(null);
      }, glitchDurationMs) as unknown as number;
      timeoutsRef.current.push(id);
    }
  };

  const runSequence = () => {
    if (runningRef.current || icons.length <= 1) return;
    runningRef.current = true;

    const total = icons.length;

    for (let i = 1; i < total; i++) {
      const id = window.setTimeout(() => {
        stepTo(i);
      }, i * stepDurationMs) as unknown as number;
      timeoutsRef.current.push(id);
    }

    // Allow re-trigger after sequence completes (one pass per hover)
    const doneId = window.setTimeout(() => {
      runningRef.current = false;
    }, total * stepDurationMs + 10) as unknown as number;
    timeoutsRef.current.push(doneId);
  };

  useImperativeHandle(ref, () => ({
    trigger: runSequence,
  }));

  const handleMouseEnter = () => {
    if (triggerMode === 'self') runSequence();
  };

  const wrapperClass = [
    'icon-morph',
    transitionStyle === 'glitch' ? 'icon-morph--glitch' : '',
    glitching ? 'do-glitch' : '',
    className,
  ].filter(Boolean).join(' ');

  const activeIconEl = React.cloneElement(icons[activeIdx], { size, strokeWidth });

  const styleProps = (transitionStyle === 'glitch')
    ? ({ ['--glitch-duration' as any]: `${glitchDurationMs}ms` } as React.CSSProperties)
    : undefined;

  return (
    <div 
      className={wrapperClass}
      style={styleProps}
      onMouseEnter={handleMouseEnter}
      aria-hidden="true"
      role="img"
    >
      {transitionStyle === 'motion' ? (
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIdx}
            className="icon-morph__motion"
            initial={{ opacity: 0, scale: 0.94, x: -14, filter: 'blur(2px)' }}
            animate={{ opacity: 1, scale: 1, x: 0, filter: 'blur(0px)' }}
            exit={{ opacity: 0, scale: 1.04, x: 14, filter: 'blur(2px)' }}
            transition={{ duration: stepDurationMs / 1000, ease: 'easeOut' }}
          >
            {activeIconEl}
          </motion.div>
        </AnimatePresence>
      ) : (
        icons.map((IconEl, idx) => (
          <div
            key={idx}
            className={['icon-morph__layer', idx === activeIdx ? 'is-active' : '', glitching && prevIdx === idx ? 'is-prev' : ''].filter(Boolean).join(' ')}
            style={transitionStyle === 'glitch' ? undefined : { transitionDuration: `${stepDurationMs}ms` }}
          >
            {React.cloneElement(IconEl, { size, strokeWidth })}
          </div>
        ))
      )}
    </div>
  );
});

export default IconMorph;

